'use client';

import { UserProfile } from '@/entities/user-profile';
import { SupportSuccessPopup } from '@/features/support-form/ui/support-success-popup';
import { ApiError } from '@/shared/api/api-client/api-error';
import { createSupportRequest } from '@/shared/api/support/create-support-request';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useTransition } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { CONTACT_TYPES } from '@/shared/constants/contact-types';
import { supportFormSchema, SupportFormInput } from '@/shared/lib/validation/support/support.schema';
import { RequiredDataNote } from '@/shared/ui/kit/required-data-note';
import { Button, Textarea } from '@/shared/ui/core';
import { ClearableInput } from '@/shared/ui/kit/clearable-input';
import { ControlledSelect } from '@/shared/ui/kit/controlled-select';
import styles from './support-form.module.scss';


type ComponentProps = {
    profile: UserProfile | null;
}


export function SupportForm ({ profile }: ComponentProps) {
    const [ isPending, startTransition ] = useTransition();
    const [ success, setSuccess ] = useState(false);

    const methods = useForm<SupportFormInput>({
        resolver     : zodResolver(supportFormSchema),
        defaultValues: {
            name        : ((profile?.first_name ?? '') + ' ' + (profile?.last_name ?? '')).trim(),
            email       : profile?.email ?? '',
            company_name: profile?.company_name ?? '',
            contact_type: '',
            description : '',
        },
    });
    const {
              register,
              handleSubmit,
              setValue,
              control,
              formState: { errors },
              setError,
          }       = methods;

    const onSubmit = handleSubmit((data) => {
        startTransition(async () => {
            try {
                await createSupportRequest(data);
                console.log(data);
                methods.reset();
                setSuccess(true);
            } catch (err) {
                if (err instanceof ApiError && err.data && typeof err.data === 'object') {
                    Object.entries(err.data).forEach(([ field, msgs ]) => {
                        if (Array.isArray(msgs)) {
                            setError(field as keyof SupportFormInput, { message: msgs[0] });
                        }
                    });
                }
            }
        });
    });

    const nameVal        = useWatch({ control, name: 'name' });
    const emailVal       = useWatch({ control, name: 'email' });
    const companyNameVal = useWatch({ control, name: 'company_name' });
    const descriptionVal = useWatch({ control, name: 'description' });

    return (
        <FormProvider { ...methods }>
            <div className={ styles.row }>
                <ClearableInput
                    label="Name:"
                    placeholder="Enter your name"
                    type="text"
                    autoComplete="name"
                    required
                    error={ errors.name?.message }
                    value={ nameVal ?? '' }
                    onClear={ () => setValue('name', '', { shouldValidate: true }) }
                    { ...register('name') }
                />

                <ClearableInput
                    label="Email:"
                    placeholder="Enter your email"
                    type="email"
                    autoComplete="email"
                    required
                    error={ errors.email?.message }
                    value={ emailVal ?? '' }
                    onClear={ () => setValue('email', '', { shouldValidate: true }) }
                    { ...register('email') }
                />
            </div>

            <div className={ styles.row }>
                <ClearableInput
                    label="Company name:"
                    placeholder="Enter your company name"
                    type="text"
                    required
                    error={ errors.company_name?.message }
                    value={ companyNameVal ?? '' }
                    onClear={ () => setValue('company_name', '', { shouldValidate: true }) }
                    { ...register('company_name') }
                />

                <ControlledSelect<SupportFormInput>
                    name="contact_type"
                    label="Contact type:"
                    options={ CONTACT_TYPES }
                    placeholder="Select contact type"
                    searchable
                    required
                />
            </div>

            <Textarea
                className={ styles.textarea }
                label="Description:"
                placeholder="Enter the description of the incident"
                required
                error={ errors.description?.message }
                value={ descriptionVal ?? '' }
                { ...register('description') }
            />

            <RequiredDataNote/>

            <Button
                loading={ isPending }
                className={ styles.button }
                onClick={ onSubmit }
            >
                Send message
            </Button>

            {
                success &&
                <SupportSuccessPopup
                    open={ success }
                    name={ nameVal ?? '' }
                    onClose={() => setSuccess(false)}
                />
            }
        </FormProvider>
    );
}
